import React, { CSSProperties, useCallback, useMemo } from "react";

import { Popup } from "semantic-ui-react";

import moment from "moment";

import { RehabDeviceSlotType } from "./RHBInterface";

type TimeSlotProps = {
  data: DeviceSlotType;
  device: Record<string, any>;
  rehabDeviceSlot?: RehabDeviceSlotType;
  selectedDevices: DeviceSlotType[];
  onSelectDevice: (data: DeviceSlotType) => void;
};

export type DeviceSlotType = {
  id?: number;
  device: number;
  device_free: boolean;
  end_datetime: string;
  rehabilitation_order_id?: number;
  start_datetime: string;
};

type Styles = Record<"slot", CSSProperties>;

const styles: Styles = {
  slot: {
    alignItems: "center",
    borderRadius: "5px",
    display: "flex",
    fontSize: "75%",
    height: "24px",
    justifyContent: "center",
    minWidth: "25px",
    width: "100%",
  },
};

const DeviceTimeSlot = (props: TimeSlotProps) => {
  const bookingStatus = useMemo(() => {
    const selected = props.selectedDevices.find(
      (acc) =>
        props.data.device === acc.device &&
        moment(props.data.start_datetime).isSame(moment(acc.start_datetime)) &&
        moment(props.data.end_datetime).isSame(moment(acc.end_datetime))
    );

    const filterAppointment = (props.rehabDeviceSlot?.rehab_order_device ?? []).filter(
      (acc) =>
        moment(`${acc.start_date} ${acc.start_time}`).isSame(moment(props.data.start_datetime)) &&
        moment(`${acc.end_date} ${acc.end_time}`).isSame(moment(props.data.end_datetime)) &&
        acc.rehab_device_id === props.data.device
    );

    const appointmentCount =
      filterAppointment.length + Number(selected?.rehabilitation_order_id ? 0 : !!selected);
    const remaining = props.device.qty_of_device - filterAppointment.length;

    return {
      appointmentCount,
      filterAppointment,
      remaining,
      selected,
    };
  }, [props.data, props.device, props.rehabDeviceSlot, props.selectedDevices]);

  const slotType = useMemo(() => {
    let type: "AVAILABLE" | "BOOKING" | "FULL" = "AVAILABLE";

    if (bookingStatus.selected) {
      type = "BOOKING";
    } else if (bookingStatus.remaining <= 0 && !props.device.allow_overbooking) {
      type = "FULL";
    }

    const bg = {
      AVAILABLE: "rgba(186, 231, 241, 1)",
      BOOKING: "rgba(0, 114, 188, 1)",
      FULL: "rgb(217, 217, 217)",
    }[type];

    return { bg, type };
  }, [bookingStatus.remaining, bookingStatus.selected, props.device.allow_overbooking]);

  const handleClick = useCallback(() => {
    if (slotType.type !== "FULL") {
      props.onSelectDevice(props.data);
    }
  }, [props.data, props.onSelectDevice, slotType.type]);

  return (
    <Popup
      style={{ borderColor: "rgba(33, 133, 208, 0.5)" }}
      disabled={!props.device.allow_overbooking}
      position="bottom center"
      content={
        <>
          <div>Allow Overbooking</div>
          {!!props.device.qty_of_device && <div>Total = {props.device.qty_of_device}</div>}
          <div>Booked = {bookingStatus.filterAppointment.length}</div>
        </>
      }
      trigger={
        <div
          style={{
            backgroundColor: slotType.bg,
            color: slotType.type === "BOOKING" ? "white" : "",
            cursor: slotType.type === "FULL" ? "" : "pointer",
            ...styles.slot,
          }}
          onClick={handleClick}
        >
          {!props.device.allow_overbooking && bookingStatus.remaining > 0 && (
            <>
              {props.device.qty_of_device - bookingStatus.appointmentCount}/
              {props.device.qty_of_device}
            </>
          )}
        </div>
      }
    />
  );
};

DeviceTimeSlot.displayName = "DeviceTimeSlot";

export default React.memo(DeviceTimeSlot);
